import React, { useRef, useEffect, useState } from 'react';
import { RocketOutlined } from '@ant-design/icons';
import { AppTheme } from '../../theme/themes';

interface BadgeShowcaseProps {
  theme: AppTheme;
}

interface PhysicsObject {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rotation: number;
  vRotation: number;
  isDragging: boolean;
  width: number;
  height: number;
}

const BadgeShowcase: React.FC<BadgeShowcaseProps> = ({ theme }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const requestRef = useRef<number | null>(null);
  
  const [badge, setBadge] = useState<PhysicsObject>({
    x: 50,
    y: 20,
    vx: 1,
    vy: 0,
    rotation: 0,
    vRotation: 0.5,
    isDragging: false,
    width: 48,
    height: 48,
  });

  // Physics constants
  const GRAVITY = 0.08;
  const BOUNCE = 0.6; 
  const FRICTION = 0.99;

  // Refs for interaction
  const dragStartRef = useRef<{ x: number; y: number } | null>(null);
  const lastMousePosRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const velocityRef = useRef<{ vx: number; vy: number }>({ vx: 0, vy: 0 });

  useEffect(() => {
    const updatePhysics = () => {
      if (!containerRef.current) {
        requestRef.current = requestAnimationFrame(updatePhysics);
        return;
      }
      
      const containerWidth = containerRef.current.clientWidth;
      const containerHeight = containerRef.current.clientHeight;

      setBadge(prev => {
        if (prev.isDragging) return prev;

        let newX = prev.x + prev.vx;
        let newY = prev.y + prev.vy;
        let newVx = prev.vx * FRICTION;
        let newVy = prev.vy + GRAVITY;
        let newRotation = prev.rotation + prev.vRotation;
        let newVRotation = prev.vRotation * FRICTION;

        // Floor collision
        if (newY + prev.height > containerHeight) {
          newY = containerHeight - prev.height;
          newVy = -newVy * BOUNCE;
          newVx *= 0.8;
          if (Math.abs(newVy) < GRAVITY * 2) {
             newVy = 0;
          }
          newVRotation = -newVRotation * 0.5;
        }

        // Ceiling collision
        if (newY < 0) {
          newY = 0;
          newVy = Math.abs(newVy) * BOUNCE;
          if (newVy < GRAVITY * 2) newVy = 0;
        }

        // Wall collisions
        if (newX + prev.width > containerWidth) {
          newX = containerWidth - prev.width;
          newVx = -newVx * BOUNCE;
        }
        if (newX < 0) {
          newX = 0;
          newVx = -newVx * BOUNCE;
        }

        if (Math.abs(newVRotation) < 0.01) newVRotation = 0;
        if (Math.abs(newVx) < 0.01) newVx = 0;

        return {
          ...prev,
          x: newX,
          y: newY,
          vx: newVx,
          vy: newVy,
          rotation: newRotation,
          vRotation: newVRotation
        };
      });

      requestRef.current = requestAnimationFrame(updatePhysics);
    };

    requestRef.current = requestAnimationFrame(updatePhysics);

    return () => {
      if (requestRef.current) cancelAnimationFrame(requestRef.current);
    };
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragStartRef.current = { x: e.clientX - badge.x, y: e.clientY - badge.y };
    lastMousePosRef.current = { x: e.clientX, y: e.clientY, time: Date.now() };
    velocityRef.current = { vx: 0, vy: 0 };
    
    setBadge(prev => ({ ...prev, isDragging: true, vx: 0, vy: 0, vRotation: 0 }));
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.addEventListener('mouseleave', handleMouseUp);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!dragStartRef.current) return;

    const now = Date.now();
    // Calculate instantaneous velocity
    if (lastMousePosRef.current) {
      const dt = now - lastMousePosRef.current.time;
      // Only update velocity if we have a valid time delta, or just use raw frame diff
      // Using raw frame diff is usually more "responsive" feeling for throw interactions
      velocityRef.current = {
        vx: e.clientX - lastMousePosRef.current.x,
        vy: e.clientY - lastMousePosRef.current.y
      };
    }

    const newX = e.clientX - dragStartRef.current.x;
    const newY = e.clientY - dragStartRef.current.y;

    setBadge(prev => ({
      ...prev,
      x: newX,
      y: newY
    }));
    
    lastMousePosRef.current = { x: e.clientX, y: e.clientY, time: now };
  };

  const handleMouseUp = (e: MouseEvent) => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    document.body.removeEventListener('mouseleave', handleMouseUp);

    const timeSinceLastMove = Date.now() - (lastMousePosRef.current?.time || 0);
    // If mouse held still for > 100ms, drop with 0 velocity
    const isStale = timeSinceLastMove > 100;

    if (!isStale) {
      const { vx, vy } = velocityRef.current;
      
      // Amplify the throw slightly to make it feel more "fast"
      // Clamping to avoid breaking the physics with massive values
      const throwVx = Math.min(Math.max(vx * 1.2, -30), 30);
      const throwVy = Math.min(Math.max(vy * 1.2, -30), 30);
      const spin = (Math.random() - 0.5) * 15; // Increased spin

      setBadge(prev => ({
        ...prev,
        isDragging: false,
        vx: throwVx,
        vy: throwVy,
        vRotation: spin
      }));
    } else {
      setBadge(prev => ({ ...prev, isDragging: false }));
    }

    dragStartRef.current = null;
    lastMousePosRef.current = null;
  };

  return (
    <div 
      ref={containerRef}
      style={{ 
        position: 'relative',
        width: '100%',
        height: '200px',
        background: theme.appBackground,
        borderRadius: '12px',
        border: `1px dashed ${theme.surfaceBorder}`,
        overflow: 'hidden',
        cursor: badge.isDragging ? 'grabbing' : 'default'
      }}
    >
      <div style={{
        position: 'absolute',
        top: '12px',
        left: '12px',
        fontSize: '12px',
        color: theme.textSecondary,
        pointerEvents: 'none',
        zIndex: 0
      }}>
      </div>

      <div
        onMouseDown={handleMouseDown}
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          transform: `translate3d(${badge.x}px, ${badge.y}px, 0) rotate(${badge.rotation}deg)`,
          width: `${badge.width}px`,
          height: `${badge.height}px`,
          background: 'rgba(251, 191, 36, 0.1)',
          border: '2px solid #fbbf24',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'grab',
          boxShadow: '0 0 15px rgba(251, 191, 36, 0.4)',
          userSelect: 'none',
          touchAction: 'none',
          zIndex: 10,
          color: '#fbbf24'
        }}
      >
        <RocketOutlined style={{ fontSize: '24px' }} />
      </div>
    </div>
  );
};

export default BadgeShowcase;
